import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExploreBackHeader, ExploreSubpageCard, useStandaloneExploreHeader } from "./areaShared";
import { getMyDialogues } from "../../Services/explore";
import PersonalDialogueRow from "./sections/PersonalDialogueRow";
import { LIBRARY_COPY, SECTION_COPY } from "./copy";
import plusIcon from "../../Assets/icon/explore/plus.svg";
import "./style.css";
import "./sections/style.css";

/**
 * Your Personal Dialogue Library (Figma 10873:24975) — "See All" from the
 * Personal Dialogues section.
 *
 * Rows open the standard Dialogue Detail by `dialogue_id` (an ObjectId the
 * Overview route accepts), never by `href`. "Capture Something New" opens
 * the Capture tab.
 */
function MyDialogues() {
  const navigate = useNavigate();
  useStandaloneExploreHeader();
  // DCI v27: `?q=` searches your own Personal Dialogues (archived included) — the
  // target of search's Personal Dialogues "See All".
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [text, setText] = useState(q);

  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => setText(q), [q]);
  useEffect(() => {
    if (text === q) return undefined;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (text.trim()) next.set("q", text.trim());
      else next.delete("q");
      setParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getMyDialogues(q);
        if (!cancelled) setState({ loading: false, data, error: false });
      } catch (e) {
        console.error("Failed to load the Personal Dialogue Library", e);
        if (!cancelled) setState({ loading: false, data: null, error: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [q]);

  const open = (item) => navigate(`/explore/dialogue/${encodeURIComponent(item.dialogue_id)}`);
  const rows = (state.data?.dialogues || []).filter((d) => d?.dialogue_id);
  // DCI v27 D-9: archived Personal Dialogues are their own group here.
  const archived = (state.data?.archived || []).filter((d) => d?.dialogue_id);

  let body;
  if (state.loading) body = <div className="explore-card explore-card--status">Loading…</div>;
  else if (state.error)
    body = <div className="explore-card explore-card--status">{LIBRARY_COPY.error}</div>;
  else if (state.data === null)
    body = <div className="explore-card explore-card--status">{LIBRARY_COPY.unavailable}</div>;
  else if (rows.length === 0 && archived.length === 0)
    body = (
      <div className="explore-card explore-card--status">
        {q ? LIBRARY_COPY.noMatch : LIBRARY_COPY.empty}
      </div>
    );
  else
    body = (
      <div className="ex-plist">
        <div className="ex-plist__head">
          <span className="ex-plist__label">{LIBRARY_COPY.listLabel}</span>
        </div>
        {rows.map((row) => (
          <PersonalDialogueRow key={row.dialogue_id} item={row} onOpen={open} />
        ))}
        {archived.length > 0 && (
          <>
            <div className="ex-plist__head">
              <span className="ex-plist__label">{LIBRARY_COPY.archived}</span>
            </div>
            {archived.map((row) => (
              <PersonalDialogueRow key={row.dialogue_id} item={row} onOpen={open} />
            ))}
          </>
        )}
        <div className="ex-plist__foot">
          <button type="button" className="ex-plist__capture" onClick={() => navigate("/capture")}>
            <img src={plusIcon} alt="" width="20" height="20" />
            {SECTION_COPY.PERSONAL_DIALOGUES.capture}
          </button>
        </div>
      </div>
    );

  return (
    <div className="explore-page">
      <div className="explore-page__inner">
        <ExploreBackHeader to="/explore" />
        <ExploreSubpageCard>
          <h1 className="explore-page__title ex-lib__title">{LIBRARY_COPY.title}</h1>
          <label className="ex-lib__search">
            <input
              type="search"
              aria-label={LIBRARY_COPY.searchLabel}
              placeholder={LIBRARY_COPY.searchLabel}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          {body}
        </ExploreSubpageCard>
      </div>
    </div>
  );
}

export default MyDialogues;
